import { useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { Formik } from "formik";

import { getFullName } from "@vega-api/custom/utils/user";
import { getGetLimitedPartnerUsersPermissionViewQueryOptions } from "@vega-api/generated/coinvest-deal-permission/coinvest-deal-permission";
import {
  useGetDefaultTemplateName,
  useGetTemplateContentsByTemplateName,
} from "@vega-api/generated/outreach-template-contents/outreach-template-contents";
import { type CoinvestPerson } from "@vega-api/generated/types/coinvestPerson";
import { type DealShortlistItemResponse } from "@vega-api/generated/types/dealShortlistItemResponse";
import {
  addSelectItemFieldsToUser,
  type SearchMultiSelectItemWithUser,
} from "@vega-core/components/Form/Controls/Input/SearchUser/SearchUserMultiselect";
import { LoadingSpinner } from "@vega-core/components/Loaders/LoadingSpinner/LoadingSpinner";
import { usePublicConfig } from "@vega-core/hooks/usePublicConfig";
import { m } from "@vega-core/paraglide/messages";

import { ContactsStep } from "./components/ContactsStep";
import { EmailContentStep } from "./components/EmailContentStep";
import { useSendEmailMutation } from "./hooks/useSendEmailMutation";
import { useSenderSearch } from "./hooks/useSenderSearch";

export enum EmailModalStep {
  CONTACTS = "contacts",
  EMAIL_CONTENT = "emailContent",
}

/**
 * Converts a SearchMultiSelectItemWithUser to CoinvestPerson
 */
const toCoinvestPerson = (
  user: SearchMultiSelectItemWithUser,
): CoinvestPerson => ({
  id: user.id,
  fullName: { firstName: user.fullName, lastName: "" },
  email: user.email,
});

export type EmailEditModalFormValues = {
  emailSubject: string;
  emailText: string;
  to: SearchMultiSelectItemWithUser[];
  cc: SearchMultiSelectItemWithUser[];
  sender: SearchMultiSelectItemWithUser | null;
};

export type EmailEditModalProps = {
  closeModal: () => void;
  prospects: DealShortlistItemResponse[];
  dealId: string;
  dealName: string;
};

export const EmailEditModal = ({
  closeModal,
  prospects,
  dealId,
  dealName,
}: EmailEditModalProps) => {
  const [step, setStep] = useState<EmailModalStep>(
    EmailModalStep.EMAIL_CONTENT,
  );

  const isMultipleProspects = prospects.length > 1;
  const publicConfig = usePublicConfig();

  const lpUsersQueries = useQueries({
    queries: prospects.map((prospect) =>
      getGetLimitedPartnerUsersPermissionViewQueryOptions(
        dealId,
        prospect.limitedPartner.id,
        {
          query: {
            select: (data) => data.data,
          },
        },
      ),
    ),
  });

  const { data: defaultTemplateNameData } = useGetDefaultTemplateName();

  const {
    data: templateContentsData,
    isPending: isTemplateContentsPending,
    isError: isTemplateContentsError,
  } = useGetTemplateContentsByTemplateName(
    defaultTemplateNameData?.data?.templateName ?? "",
    {
      query: {
        enabled: !!defaultTemplateNameData?.data?.templateName,
      },
    },
  );

  const shortlistItemToLimitedPartnerMap = prospects.reduce(
    (acc, prospect) => {
      if (prospect.limitedPartner.id) {
        acc[prospect.id] = prospect.limitedPartner.id;
      }

      return acc;
    },
    {} as Record<string, string | undefined>,
  );

  const {
    sendBulkEmail,
    sendSingleEmail,
    isPending: isSendEmailPending,
  } = useSendEmailMutation({
    dealId,
    onSuccess: closeModal,
    shortlistItemToLimitedPartnerMap,
  });

  const areAnyLpUsersQueriesPending = lpUsersQueries.some(
    (query) => query.isPending,
  );

  const areAnyLpUsersQueriesError = lpUsersQueries.some(
    (query) => query.isError,
  );

  const prospectsWithUserList = prospects.map((prospect, index) => ({
    ...prospect,
    users: lpUsersQueries[index].data?.users ?? [],
  }));

  const firstProspect = prospectsWithUserList[0];
  const firstProspectAllUsersSearchItems = firstProspect.users.map(
    addSelectItemFieldsToUser,
  );
  const firstProspectRelationshipSelectedItem = firstProspect.relationship
    ? addSelectItemFieldsToUser({
        id: firstProspect.relationship.id,
        fullName: getFullName(firstProspect.relationship.fullName),
        email: firstProspect.relationship.email,
      })
    : null;

  const {
    gpUsersSearchItems: gpUsersWithFirstProspectRelationshipSearchItems,
    onSenderSearchChange,
    isSenderSearchLoading,
    senderSearchValue,
  } = useSenderSearch({
    dealId,
    selectedItemToInclude: firstProspectRelationshipSelectedItem,
  });

  const hasAnyLockedProspects = prospectsWithUserList.some(
    (prospect) => prospect.isPermissionLocked,
  );

  const initialValues: EmailEditModalFormValues = isMultipleProspects
    ? {
        emailSubject: templateContentsData?.data?.templateHeaders.subject ?? "",
        emailText: templateContentsData?.data?.textPart ?? "",
        to: [],
        cc: [],
        sender: null,
      }
    : {
        emailSubject: m.coInvestments_editEmail_defaultSubject({
          dealName,
          gpName: publicConfig.global.companyName,
          lpName: firstProspect.limitedPartner.name,
        }),
        emailText: m.coInvestments_editEmail_defaultTemplate({
          lpName: firstProspect.limitedPartner.name,
          gpName: publicConfig.global.companyName,
          dealLink: firstProspect.dealLink,
        }),
        to: firstProspectAllUsersSearchItems,
        cc: [],
        sender: firstProspectRelationshipSelectedItem,
      };

  const validateForm = (values: EmailEditModalFormValues) => {
    const errors: Partial<Record<keyof EmailEditModalFormValues, string>> = {};

    if (!values.emailSubject.trim()) {
      errors.emailSubject =
        m.coInvestments_editEmail_validation_subjectRequired();
    }

    if (!values.emailText.trim()) {
      errors.emailText = m.coInvestments_editEmail_validation_required();
    }

    if (!isMultipleProspects && values.to.length === 0) {
      errors.to = m.coInvestments_editEmail_validation_toRequired();
    }

    if (!isMultipleProspects && !values.sender) {
      errors.sender = m.coInvestments_editEmail_validation_fromRequired();
    }

    return errors;
  };

  const handleSubmit = (values: EmailEditModalFormValues) => {
    if (isMultipleProspects) {
      sendBulkEmail(prospectsWithUserList.map((p) => p.id));
    } else {
      sendSingleEmail({
        dealShortlistItemId: firstProspect.id,
        emailBody: values.emailText,
        emailSubject: values.emailSubject,
        mainRecipients: values.to.map(toCoinvestPerson),
        ccRecipients: values.cc.map(toCoinvestPerson),
        opportunityOwner: toCoinvestPerson(values.sender!),
      });
    }
  };

  const handleBack = () => {
    setStep(EmailModalStep.EMAIL_CONTENT);
  };

  const handleNext = () => {
    setStep(EmailModalStep.CONTACTS);
  };

  const isLoading = areAnyLpUsersQueriesPending || isTemplateContentsPending;
  const isError = areAnyLpUsersQueriesError || isTemplateContentsError;

  if (isLoading || isError) {
    return <LoadingSpinner />;
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={validateForm}
    >
      {({ submitForm }) => (
        <>
          {step === EmailModalStep.EMAIL_CONTENT && (
            <EmailContentStep
              closeModal={closeModal}
              dealLink={firstProspect.dealLink}
              firstProspectLpsUsers={firstProspectAllUsersSearchItems}
              gpUsers={gpUsersWithFirstProspectRelationshipSearchItems}
              hasAnyLockedProspects={hasAnyLockedProspects}
              isMultipleProspects={isMultipleProspects}
              isSendEmailPending={isSendEmailPending}
              isSenderSearchLoading={isSenderSearchLoading}
              onNext={handleNext}
              onSenderSearchChange={onSenderSearchChange}
              senderSearchValue={senderSearchValue}
            />
          )}
          {step === EmailModalStep.CONTACTS && (
            <ContactsStep
              closeModal={closeModal}
              isSendEmailPending={isSendEmailPending}
              onBack={handleBack}
              onSend={submitForm}
              prospectsWithUserList={prospectsWithUserList}
            />
          )}
        </>
      )}
    </Formik>
  );
};
